import React, { useState, useEffect, useCallback, useRef } from 'react';
import { IonPage, IonContent, useIonRouter } from '@ionic/react';
import { getProperties, getAgents } from '../services/mysteryShopService';
import type { MysteryShopProperty, MysteryShopAgent } from '../types/mysteryShop';
import { DashboardView } from '../components/mystery-shop/dashboard/dashboard-view';
import { AllProperties } from '../components/mystery-shop/properties/all-properties';
import { PropertyDetail } from '../components/mystery-shop/properties/property-detail';
import { AllAgents } from '../components/mystery-shop/agents/all-agents';
import { AgentDetail } from '../components/mystery-shop/agents/agent-detail';
import { CallDetail } from '../components/mystery-shop/call-detail';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useRefresh } from '../contexts/RefreshContext';
import { useProfileStore } from '../store/useProfileStore';
import { isTeamOwnerOrAdmin } from '../types/profile';

// ============================================================================
// LOCAL TYPES
// ============================================================================

type ViewType = 'dashboard' | 'properties' | 'propertyDetail' | 'callDetail' | 'agents' | 'agentDetail';

interface Breadcrumb {
  label: string;
  onClick?: () => void;
}

// ============================================================================
// NAVIGATION
// ============================================================================

interface NavigationProps {
  currentView: ViewType;
  onNavigate: (view: ViewType) => void;
  breadcrumbs?: Breadcrumb[];
}

const Navigation: React.FC<NavigationProps> = ({ currentView, onNavigate, breadcrumbs }) => (
  <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
    <div className="max-w-7xl mx-auto px-6 py-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-6">
          <button onClick={() => onNavigate('dashboard')} className="font-bold text-xl text-gray-800 hover:text-gray-600 transition-colors">Mystery Shop</button>

          {breadcrumbs && breadcrumbs.length > 0 && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-300">/</span>
              {breadcrumbs.map((crumb, idx) => (
                <React.Fragment key={idx}>
                  {crumb.onClick ? (
                    <button
                      onClick={crumb.onClick}
                      className="text-gray-500 hover:text-gray-800 transition-colors"
                    >
                      {crumb.label}
                    </button>
                  ) : (
                    <span className="text-gray-800 font-medium">{crumb.label}</span>
                  )}
                  {idx < breadcrumbs.length - 1 && <span className="text-gray-300">/</span>}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>

        <nav className="flex items-center gap-1">
          {[
            { id: 'dashboard' as const, label: 'Dashboard' },
            { id: 'properties' as const, label: 'Properties' },
            { id: 'agents' as const, label: 'Agents' }
          ].map(item => (
            <button
              key={item.id}
              onClick={() => onNavigate(item.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                currentView === item.id
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {item.label}
            </button>
          ))}
        </nav>
      </div>
    </div>
  </header>
);

// ============================================================================
// PAGE
// ============================================================================

export default function MysteryShopPage() {
  const [properties, setProperties] = useState<MysteryShopProperty[]>([]);
  const [agents, setAgents] = useState<MysteryShopAgent[]>([]);
  const [view, setView] = useState<ViewType>('dashboard');
  const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(null);
  const [selectedCallId, setSelectedCallId] = useState<string | null>(null);
  const [selectedCallAgentName, setSelectedCallAgentName] = useState<string | null>(null);
  const [selectedCallAgentId, setSelectedCallAgentId] = useState<string | null>(null);
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [dataKey, setDataKey] = useState(0);
  const contentRef = useRef<HTMLIonContentElement>(null);

  const isDrawerMode = useMediaQuery('(min-width: 1024px)');
  const router = useIonRouter();
  const { registerRefreshHandler, unregisterRefreshHandler } = useRefresh();
  const { activeProfile, activeTeam } = useProfileStore();
  const hasShopRole = activeProfile?.roles?.some(r => r === 'shop_owner' || r === 'shop_admin' || r === 'shop_user') && !!activeTeam?.clientId;

  // Redirect non-owner/admin users to dashboard
  useEffect(() => {
    if (activeProfile && !isTeamOwnerOrAdmin(activeProfile)) {
      router.push('/dashboard', 'none');
    }
  }, [activeProfile, router]);

  const fetchData = useCallback(async () => {
    // Reset to dashboard and clear selections (e.g. when toggling mock data)
    setView('dashboard');
    setSelectedPropertyId(null);
    setSelectedCallId(null);
    setSelectedCallAgentName(null);
    setSelectedCallAgentId(null);
    setSelectedAgentId(null);
    setDrawerOpen(false);

    await Promise.all([getProperties(), getAgents()]).then(([p, a]) => {
      setProperties(p);
      setAgents(a);
    });
    setDataKey(k => k + 1);
  }, []);

  // Scroll to top on view changes
  useEffect(() => {
    contentRef.current?.scrollToTop(0);
  }, [view]);

  // Load reference data on mount
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Register refresh handler for Test Panel
  useEffect(() => {
    registerRefreshHandler('/mystery-shop', fetchData);
    return () => {
      unregisterRefreshHandler('/mystery-shop');
    };
  }, [registerRefreshHandler, unregisterRefreshHandler, fetchData]);

  const handleNavigate = (newView: ViewType) => {
    setView(newView);
    setSelectedPropertyId(null);
    setSelectedCallId(null);
    setSelectedCallAgentName(null);
    setSelectedCallAgentId(null);
    setSelectedAgentId(null);
    setDrawerOpen(false);
  };

  const handleSelectProperty = (propertyId: string | null) => {
    if (propertyId === null) {
      setView('properties');
    } else {
      setSelectedPropertyId(propertyId);
      setView('propertyDetail');
    }
    setSelectedCallId(null);
    setDrawerOpen(false);
  };

  const handleSelectCall = useCallback((callId: string, agentName?: string, agentId?: string) => {
    setSelectedCallId(callId);
    setSelectedCallAgentName(agentName ?? null);
    setSelectedCallAgentId(agentId ?? null);
    if (isDrawerMode) {
      // Drawer mode: keep source view, animate drawer in
      requestAnimationFrame(() => setDrawerOpen(true));
    } else {
      setView('callDetail');
    }
  }, [isDrawerMode]);

  const closeCallDetail = useCallback(() => {
    setDrawerOpen(false);
    setSelectedCallId(null);
    setSelectedCallAgentName(null);
    setSelectedCallAgentId(null);
  }, []);

  const handleSelectAgent = (agentId: string) => {
    setSelectedAgentId(agentId);
    setSelectedPropertyId(null);
    setView('agentDetail');
    setSelectedCallId(null);
    setDrawerOpen(false);
  };

  // Navigate to agent detail while keeping the property context in the breadcrumb
  const handleSelectPropertyAgent = (agentId: string) => {
    setSelectedAgentId(agentId);
    setView('agentDetail');
    // selectedPropertyId intentionally not cleared
    setSelectedCallId(null);
    setDrawerOpen(false);
  };

  const selectedProperty = properties.find(p => p.id === selectedPropertyId);
  const selectedAgent = agents.find(a => a.id === selectedAgentId);

  const getBreadcrumbs = (): Breadcrumb[] => {
    switch (view) {
      case 'propertyDetail':
        return [
          { label: 'Properties', onClick: () => handleNavigate('properties') },
          { label: selectedProperty?.name || '' }
        ];
      case 'callDetail':
        if (selectedPropertyId && selectedProperty) {
          return [
            { label: 'Properties', onClick: () => handleNavigate('properties') },
            { label: selectedProperty.name, onClick: () => handleSelectProperty(selectedPropertyId) },
            ...(selectedCallAgentName ? [{
              label: selectedCallAgentName,
              onClick: selectedCallAgentId ? () => handleSelectPropertyAgent(selectedCallAgentId) : undefined
            }] : []),
            { label: 'Call' }
          ];
        }
        if (selectedAgentId && selectedAgent) {
          return [
            { label: 'Agents', onClick: () => handleNavigate('agents') },
            { label: selectedAgent.name, onClick: () => handleSelectAgent(selectedAgentId) },
            { label: 'Call' }
          ];
        }
        return [];
      case 'agentDetail':
        if (selectedPropertyId && selectedProperty) {
          return [
            { label: 'Properties', onClick: () => handleNavigate('properties') },
            { label: selectedProperty.name, onClick: () => handleSelectProperty(selectedPropertyId) },
            { label: selectedAgent?.name || '' }
          ];
        }
        return [
          { label: 'Agents', onClick: () => handleNavigate('agents') },
          { label: selectedAgent?.name || '' }
        ];
      default:
        return [];
    }
  };

  const currentNavView: ViewType = ['dashboard', 'properties', 'agents'].includes(view)
    ? view as ViewType
    : (view === 'propertyDetail' || view === 'callDetail' || (view === 'agentDetail' && !!selectedPropertyId)
      ? 'properties'
      : 'agents');

  const showDrawer = isDrawerMode && !!selectedCallId;

  return (
    <IonPage>
      <IonContent ref={contentRef} className="bg-gray-50">
        <div className="min-h-screen bg-gray-50">
          <Navigation
            currentView={currentNavView}
            onNavigate={handleNavigate}
            breadcrumbs={getBreadcrumbs()}
          />

          <main className="max-w-7xl mx-auto px-6 py-6">
            {view === 'dashboard' && (
              <DashboardView key={dataKey} onSelectProperty={handleSelectProperty} onSelectCall={handleSelectCall} />
            )}
            {view === 'properties' && (
              <AllProperties key={dataKey} onSelectProperty={handleSelectProperty} />
            )}
            {view === 'propertyDetail' && selectedPropertyId && (
              <PropertyDetail key={dataKey} propertyId={selectedPropertyId} onSelectCall={handleSelectCall} onSelectAgent={handleSelectPropertyAgent} />
            )}
            {view === 'callDetail' && !isDrawerMode && selectedCallId && (
              <CallDetail key={dataKey} callId={selectedCallId} />
            )}
            {view === 'agents' && (
              <AllAgents key={dataKey} onSelectAgent={handleSelectAgent} />
            )}
            {view === 'agentDetail' && selectedAgentId && (
              <AgentDetail key={dataKey} agentId={selectedAgentId} onSelectCall={handleSelectCall} onSelectProperty={handleSelectProperty} />
            )}
          </main>

            {!hasShopRole && (
              <div className="max-w-7xl mx-auto px-6 pb-8 pt-8 flex justify-center">
                <div className="p-4 bg-white rounded-lg border border-gray-200">
                  <span className="text-gray-700">Want to test your team first? </span>
                  <a
                    href="https://www.rev-leasing.com/free-mystery-shop"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#D16972] font-semibold hover:underline"
                  >
                    Try our free AI mystery shop →
                  </a>
                </div>
              </div>
            )}
        </div>

        {/* Call detail drawer (wide viewports only) */}
        {showDrawer && (
          <div
            className={`fixed inset-0 bg-black/20 z-40 transition-opacity duration-300 ${drawerOpen ? 'opacity-100' : 'opacity-0'}`}
            onClick={closeCallDetail}
          />
        )}
        <div
          className={`fixed top-0 right-0 h-full w-[768px] max-w-full bg-gray-50 z-50 shadow-2xl
            overflow-y-auto transition-transform duration-300 ease-in-out
            ${showDrawer && drawerOpen ? 'translate-x-0' : 'translate-x-full'}`}
        >
          {showDrawer && selectedCallId && (
            <div className="p-6">
              <CallDetail callId={selectedCallId} onClose={closeCallDetail} />
            </div>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
}
